// MODELUL INSTALAȚIILOR, cum îl desenează pagina «Instalații».
//
// E documentul soluției `mep` (formatul 3.23 §42: `MepSegment`, `MepEquipment`,
// regulile `INS.*`), adus în memorie de `dinDocument.ts`. Traseele și
// verdictele le face motorul de pe server (`mep/`); aici doar se desenează.
//
// Coordonatele sunt ale planului: după `northUp` și `laOrigineLocala`, adică
// +x est, +y SUD, în metri față de originea locală a desenului — cadrul în care
// stau și `StoreyPlan`-urile. `z` e cota ABSOLUTĂ, în metri față de ±0,00.

import type { Cheie } from "../i18n";

export type Sistem =
  | "apa_rece" | "apa_calda" | "canalizare" | "pluviale"
  | "incendiu" | "electrice" | "incalzire" | "ventilatie";

/** Pe unde trece un segment. Decide verificările (în șapă nu se admit
 *  îmbinări, art. 15.59) și desenul (în șapă se trage punctat). */
export type Mediu = "placare" | "sapa" | "ghena" | "plafon" | "aparent" | "ingropat";

/** Ce rol are segmentul în rețeaua lui: ramura WC-ului e separată de ramura
 *  obiectelor mici (plan §7.1), coloana e verticală, iar apa ajunge la peretele
 *  umed printr-o țeavă întreagă în șapă și se împarte pe perete (plan §7.2). */
export type Rol =
  | "ramura_wc" | "ramura_obiecte" | "racord" | "coloana"
  | "distributie" | "intrare_sapa" | "tur" | "retur" | "circuit" | "canal" | "bucla"
  | "alimentare" | "principala" | "bransament" | "colector" | "racord_retea" | "ventilatie_primara";

/** Materialul, ca un cod: numele lui stă în catalog (`inst.material.*`), ca să
 *  se scrie în limba ecranului, iar devizul să-l poată lega de un articol. */
export type Material = "pp" | "pe_x" | "pe_xa" | "ppr" | "tabla_zincata" | "cablu_cyy" | "cablu_tub";

export type Punct3 = [number, number, number];

export interface Segment {
  id: string;
  sistem: Sistem;
  rol: Rol;
  /** Nivelul pe care stă (`StoreyPlan.id`); coloanele au nivelul de jos. */
  storey: string | null;
  puncte: Punct3[];
  /** Diametrul nominal, în mm (DN 100, PE-X 20…); la cablul unui
   *  apartament, secțiunea în mm²; 0 la celelalte cabluri. */
  dn: number;
  /** Diametrul exterior desenat, în metri (cu izolația). */
  d_m: number;
  /** Secțiunea dreptunghiulară, la canale de aer și paturi de cabluri. */
  sectiune?: { l: number; h: number };
  /** Panta, la canalizare (2 % = 0.02). */
  panta?: number;
  mediu: Mediu;
  material: Material;
  /** Apartamentul (`Flat`), când segmentul e al unuia. */
  flat?: string | null;
  /** Piesele (`Furniture`) pe care le deservește: legătura pe care o citesc
   *  verificările, în loc s-o ghicească din geometrie. */
  piese?: string[];
  /** Coloana (id de segment) în care se varsă o ramură de canalizare. */
  spre?: string;
}

export type FelEchipament =
  | "ghena_propusa" | "contor" | "modul_termic" | "distribuitor" | "tablou"
  | "radiator" | "grila" | "piesa_curatire" | "camin_bransament" | "bmpt" | "firida_contorizare"
  | "centrala" | "boiler" | "camin_racord";

export interface Echipament {
  id: string;
  sistem: Sistem;
  fel: FelEchipament;
  storey: string | null;
  /** Centrul cutiei, cota absolută a bazei ei în `z`. */
  centru: Punct3;
  /** Lungime (de-a lungul lui `deg`), lățime, înălțime, în metri. */
  dim: [number, number, number];
  deg?: number;
  flat?: string | null;
}

export type Verdict = "pass" | "warn" | "fail";

export interface Verificare {
  /** Id-ul permanent al regulii (`INS.SAN.WC_SEPARAT`), din anexa §6.10. */
  regula: string;
  verdict: Verdict;
  /** Ce s-a găsit, în cuvinte; cheia de text și parametrii ei. */
  mesaj: Cheie;
  param?: Record<string, string | number>;
  /** Articolul din normativ: „I 9-2022 art. 18.4”. */
  sursa: string;
  /** Segmentele și echipamentele vinovate, ca să fie evidențiate. */
  elemente: string[];
}

/** O valoare pe care generatorul a folosit-o și de unde a luat-o (plan §2):
 *  ce e implicit sau sugerat se arată „de confirmat”. */
export interface Intrare {
  cheie: Cheie;
  /** Valoarea, ca text de catalog: o mărime (`inst.val.metri`) sau o alegere. */
  valoare: Cheie;
  param?: Record<string, string | number>;
  sursa: "document" | "structura" | "implicit" | "sugerat";
}

export interface ModelInstalatii {
  segmente: Segment[];
  echipamente: Echipament[];
  verificari: Verificare[];
  intrari: Intrare[];
}

/** Sistemele, în ordinea de așezare la intersecții (I 9-2022 art. 15.37:
 *  întâi ce e gravitațional și gros), cu culoarea lor — aceeași în plan, în
 *  3D și în desenele din documentul de design. */
export const SISTEME: readonly { sistem: Sistem; culoare: string; nume: Cheie }[] = [
  { sistem: "canalizare", culoare: "#8B5A2B", nume: "inst.sistem.canalizare" },
  { sistem: "pluviale", culoare: "#2E8B57", nume: "inst.sistem.pluviale" },
  { sistem: "ventilatie", culoare: "#78909C", nume: "inst.sistem.ventilatie" },
  { sistem: "incalzire", culoare: "#E67E22", nume: "inst.sistem.incalzire" },
  { sistem: "apa_rece", culoare: "#2F6FD6", nume: "inst.sistem.apa_rece" },
  { sistem: "apa_calda", culoare: "#D64545", nume: "inst.sistem.apa_calda" },
  { sistem: "incendiu", culoare: "#B71C1C", nume: "inst.sistem.incendiu" },
  { sistem: "electrice", culoare: "#7E57C2", nume: "inst.sistem.electrice" },
];

export type Categorie = "apa" | "ventilatie" | "electrice" | "incendiu";

/** STRATURILE PE CATEGORII: fiecare categorie se aprinde întreagă, iar
 *  subcategoriile ei (sistemele) una câte una. Tot ce curge prin țevi cu apă —
 *  rece, caldă, canalizare, pluviale, încălzire — stă sub «Apă». Gruparea e
 *  numai a desenului: fiecare sistem își păstrează regulile lui. */
export const CATEGORII: readonly { categorie: Categorie; nume: Cheie; sisteme: readonly Sistem[] }[] = [
  { categorie: "apa", nume: "inst.categorie.apa",
    sisteme: ["apa_rece", "apa_calda", "canalizare", "pluviale", "incalzire"] },
  { categorie: "ventilatie", nume: "inst.categorie.ventilatie", sisteme: ["ventilatie"] },
  { categorie: "electrice", nume: "inst.categorie.electrice", sisteme: ["electrice"] },
  { categorie: "incendiu", nume: "inst.categorie.incendiu", sisteme: ["incendiu"] },
];

export const CULOARE: Record<Sistem, string> = Object.fromEntries(
  SISTEME.map((s) => [s.sistem, s.culoare])) as Record<Sistem, string>;

/** Lungimea unei polilinii 3D, în metri. */
export function lungime(p: readonly Punct3[]): number {
  let L = 0;
  for (let i = 1; i < p.length; i++) {
    L += Math.hypot(p[i][0] - p[i - 1][0], p[i][1] - p[i - 1][1], p[i][2] - p[i - 1][2]);
  }
  return L;
}

/** Cantitățile pe sistem și diametru: metri liniari, cum intră în deviz. */
export function cantitati(m: ModelInstalatii): { sistem: Sistem; dn: number; ml: number }[] {
  const acc = new Map<string, { sistem: Sistem; dn: number; ml: number }>();
  for (const s of m.segmente) {
    const k = `${s.sistem}|${s.dn}`;
    const e = acc.get(k) ?? { sistem: s.sistem, dn: s.dn, ml: 0 };
    e.ml += lungime(s.puncte);
    acc.set(k, e);
  }
  const ordine = SISTEME.map((s) => s.sistem);
  return [...acc.values()].sort((a, b) =>
    ordine.indexOf(a.sistem) - ordine.indexOf(b.sistem) || b.dn - a.dn);
}
